import { useEffect, useState } from 'react';
import { FormikProps } from 'formik';
import debounce from 'lodash/debounce';

// MUI Theme
import { TextField, Button, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { Autocomplete, createFilterOptions } from '@mui/material';

// Services
import { getLabels } from '../../../services/label.service';

// Types
import { AutoCompleteConfig } from '../../../constants/Constants';
import { FilterableTypes } from '../../../types/FilterableTypes';
import { ResourceType } from '../../../types/ResourceType';
import { Label } from '../../../types/Label';

const filter = createFilterOptions();

interface LabelTagProps {
  formRef: FormikProps<any>;
  defaultLabels?: string[];
  enableCustomLabel: boolean;
  disabled?: boolean;
  resourceType: ResourceType;
  width?: number;
}

export default function LabelTag(props: LabelTagProps): JSX.Element {
  const [labelOptions, setLabelOptions] = useState<Label[]>([]);
  const [selectedLabels, setSelectedLabels] = useState<Array<any>>([]);
  const [selectedAddTag, setSelectedAddTag] = useState<any>(null);
  const [openDialog, setOpenDialog] = useState<any>(false);

  const fetchLabels = async (value: string, isInitial) => {
    const filters: any = [{ resourceType: props.resourceType, filterType: FilterableTypes.Equals }];
    if (value) {
      filters.push({ name: value, filterType: FilterableTypes.Contains });
    }
    await getLabels({
      skip: 0,
      take: 100,
      filters: filters,
    } as any)
      .then((res: any) => {
        if (res.status === 200) {
          setLabelOptions(res.data.results);
          if (props.defaultLabels && props.defaultLabels.length > 0 && isInitial) {
            setSelectedLabels(res.data.results.filter((v: Label) => props.defaultLabels?.includes(v.id)));
          }
        }
      })
      .catch((error) => {
        console.error('error', error);
      });
  };

  useEffect(() => {
    fetchLabels('', true);
  }, []);

  const debouncedFetchOptions = debounce(async (value) => {
    await fetchLabels(value, false);
  }, 700);

  return (
    <div>
      <Autocomplete
        id='label'
        disabled={props.disabled}
        options={labelOptions}
        defaultValue={[]}
        filterSelectedOptions
        value={selectedLabels}
        multiple
        size='small'
        getOptionLabel={(option: any) => {
          if (!option) return;

          return option.name;
        }}
        style={{ width: props.width ?? 190 }}
        renderInput={(params) => {
          return <TextField {...params} variant='outlined' size='small' style={{ width: '100%' }} />;
        }}
        renderOption={(props, option) => (
          <span {...props} key={option.id}>
            {option.name}
          </span>
        )}
        onInputChange={(event, value, reason) => {
          if (reason === 'input' && value) {
            debouncedFetchOptions(value);
          }
        }}
        onClose={(event, reason: any) => {
          fetchLabels('', false); //fetch default labels
        }}
        onChange={(event, value: any, reason: any) => {
          const lastItem = value[value.length - 1];
          switch (reason) {
            case AutoCompleteConfig.EventType.Clear:
              setSelectedLabels([]);
              break;
            case AutoCompleteConfig.EventType.RemoveOption:
              setSelectedLabels(value);
              break;
            case AutoCompleteConfig.EventType.SelectOption:
              if (lastItem.type) {
                setSelectedAddTag(lastItem);
                setOpenDialog(true);
                break;
              }
              if (!selectedLabels.some((tagItem) => tagItem.id === lastItem.id)) {
                setSelectedLabels(value);
              }
              break;
            default:
              break;
          }

          props.formRef.setFieldValue(
            'labelIds',
            value.map((v) => v.id),
          );
        }}
        freeSolo
        selectOnFocus
        handleHomeEndKeys
        filterOptions={(options, params) => {
          const filtered = filter(options, params);
          if (params.inputValue !== '' && props.enableCustomLabel) {
            filtered.push({
              type: AutoCompleteConfig.OptionType.New,
              value: params.inputValue,
              name: `Add "${params.inputValue}"`,
            });
          }
          return filtered;
        }}
      />
      <Dialog
        open={openDialog}
        onClose={() => {
          setSelectedAddTag(null);
          setOpenDialog(false);
        }}
      >
        <DialogTitle>Confirm</DialogTitle>
        <DialogContent>Do you want to add this option {selectedAddTag?.value ?? ''}?</DialogContent>
        <DialogActions>
          <Button
            variant='contained'
            color='primary'
            onClick={() => {
              let maxId = 1;
              for (let i = 0; i < selectedLabels.length; i++) {
                const item = selectedLabels[i];
                if (item.id > maxId) {
                  maxId = item.id;
                }
              }
              setSelectedLabels([...selectedLabels, { id: maxId + 1, name: selectedAddTag.value }]);
              setOpenDialog(false);
            }}
          >
            Add
          </Button>
          <Button
            variant='contained'
            onClick={() => {
              setSelectedAddTag(null);
              setOpenDialog(false);
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
